import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getAccommodationById, updateAccommodation, deleteAccommodation } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Home,
  DollarSign,
  Phone,
  Save,
  MapPin,
  Calendar,
  BedDouble,
  Bath,
  Mail,
  CheckCircle2,
  Trash2,
  AlertCircle,
} from "lucide-react";

interface AccommodationFormData {
  title: string;
  description: string;
  price: string;
  period: "MONTH" | "WEEK" | "YEAR" | "day";
  type: "APARTMENT" | "HOUSE" | "ROOM" | "SHARED" | "HOSTEL" | "OTHER";
  bedrooms: string;
  bathrooms: string;
  furnished: boolean;
  address: string;
  contactEmail: string;
  contactPhone: string;
  availableFrom: string;
}

const PROPERTY_TYPES = [
  { value: "APARTMENT", label: "Apartment" },
  { value: "HOUSE", label: "House" },
  { value: "ROOM", label: "Room" },
  { value: "SHARED", label: "Shared" },
  { value: "HOSTEL", label: "Dorm" },
  { value: "OTHER", label: "Other" },
];

const PERIOD_OPTIONS = [
  { value: "MONTH", label: "Monthly" },
  { value: "WEEK", label: "Weekly" },
  { value: "day", label: "Daily" },
];

export default function EditAccommodationPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const { data: accommodation, isLoading } = useQuery(getAccommodationById, { itemId: itemId! });
  const updateAccommodationFn = useAction(updateAccommodation);
  const deleteAccommodationFn = useAction(deleteAccommodation);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<AccommodationFormData>({
    title: "",
    description: "",
    price: "",
    period: "MONTH",
    type: "APARTMENT",
    bedrooms: "",
    bathrooms: "",
    furnished: false,
    address: "",
    contactEmail: "",
    contactPhone: "",
    availableFrom: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (accommodation) {
      const meta = accommodation.metadata as any;
      setFormData({
        title: accommodation.title || "",
        description: accommodation.description || "",
        price: meta?.price ? meta.price.toString() : "",
        period: meta?.period || "MONTH",
        type: meta?.type || "APARTMENT",
        bedrooms: meta?.bedrooms ? meta.bedrooms.toString() : "",
        bathrooms: meta?.bathrooms ? meta.bathrooms.toString() : "",
        furnished: meta?.furnished || false,
        address: meta?.address || "",
        contactEmail: meta?.contactEmail || (meta?.contactInfo?.includes("@") ? meta.contactInfo.split("|")[0].trim() : ""),
        contactPhone: meta?.contactPhone || (meta?.contactInfo?.includes("|") ? meta.contactInfo.split("|")[1].trim() : ""),
        availableFrom: meta?.availableFrom ? new Date(meta.availableFrom).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
      });
    }
  }, [accommodation]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof AccommodationFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.price.trim()) newErrors.price = "Price is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await updateAccommodationFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        period: formData.period,
        type: formData.type,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        furnished: formData.furnished,
        address: formData.address.trim(),
        contactInfo: `${formData.contactEmail}${formData.contactPhone ? ` | ${formData.contactPhone}` : ""}`,
        availableFrom: new Date(formData.availableFrom).toISOString(),
      });
      handleGoBack();
    } catch (error) {
      console.error("Error updating accommodation:", error);
      setErrors({ submit: "Failed to update accommodation." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    try {
      await deleteAccommodationFn({ itemId: itemId! });
      handleGoBack();
    } catch (error) {
      console.error("Error deleting listing:", error);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/accommodation`);
    } else {
      navigate("/accommodation");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to edit listings.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Listing...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your listing...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!accommodation) {
    return (
      <IndividualEditPageLayout title="Listing Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Listing Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The listing you're looking for doesn't exist.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="accommodation-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Updating..." : "Update Listing"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Property Status" hideOnMobile>
        <div className="flex bg-slate-100 p-1 rounded-xl w-full">
          <button
            type="button"
            onClick={() => handleChange("furnished", true)}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.furnished ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Furnished
          </button>
          <button
            type="button"
            onClick={() => handleChange("furnished", false)}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              !formData.furnished ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Unfurnished
          </button>
        </div>
      </SidebarSection>
      <SidebarSection title="Danger Zone" hideOnMobile>
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete Listing
        </button>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Accommodation"
      subtitle={`Administering ${formData.title}`}
      icon={Home}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="accommodation-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div className="flex bg-slate-100 p-1 rounded-xl w-fit overflow-x-auto no-scrollbar">
            {PROPERTY_TYPES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleChange("type", opt.value)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  formData.type === opt.value ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Listing Title</label>
            <div className="relative">
              <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><DollarSign className="w-4 h-4 text-emerald-600" /> Pricing</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Rent ($)</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500/20 transition-all font-semibold"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Period</label>
                <select
                  value={formData.period}
                  onChange={(e) => handleChange("period", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500/20 transition-all"
                >
                  {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Available From</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={formData.availableFrom}
                  onChange={(e) => handleChange("availableFrom", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><MapPin className="w-4 h-4 text-blue-600" /> Location</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block"><BedDouble className="inline w-3 h-3 mb-1" /> Bedrooms</label>
                <input
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => handleChange("bedrooms", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block"><Bath className="inline w-3 h-3 mb-1" /> Bathrooms</label>
                <input
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => handleChange("bathrooms", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Address</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><CheckCircle2 className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={formData.contactEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                placeholder="(XXX) XXX-XXXX"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
